// Shadcn-io Choicebox component (Item-based)
import type { ComponentProps } from 'react';
import * as React from 'react';
import { CheckIcon } from 'lucide-react';
import { RadioGroup as RadioGroupPrimitive } from 'radix-ui';
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/shared/components/ui/item';
import { cn } from '@/shared/utils/cn';
import { RadioGroup } from '@/shared/components/ui/radio-group';

export type ChoiceboxProps = ComponentProps<typeof RadioGroup>;

export const Choicebox = ({ className, ...props }: ChoiceboxProps) => (
  <RadioGroup className={cn('flex flex-col gap-3', className)} {...props} />
);

export type ChoiceboxItemProps = RadioGroupPrimitive.RadioGroupItemProps & {
  variant?: 'default' | 'outline' | 'muted';
  size?: 'default' | 'sm';
};

export const ChoiceboxItem = ({
  className,
  children,
  variant = 'default',
  size = 'default',
  ...props
}: ChoiceboxItemProps) => (
  <RadioGroupPrimitive.Item asChild className={cn('cursor-pointer')} {...props}>
    <Item variant={variant} size={size} className={className}>
      {children}
    </Item>
  </RadioGroupPrimitive.Item>
);

export type ChoiceboxItemMediaProps = ComponentProps<typeof ItemMedia>;

export const ChoiceboxItemMedia = ({
  variant = 'default',
  ...props
}: ChoiceboxItemMediaProps) => <ItemMedia variant={variant} {...props} />;

export type ChoiceboxItemContentProps = ComponentProps<typeof ItemContent>;

export const ChoiceboxItemContent = (props: ChoiceboxItemContentProps) => (
  <ItemContent {...props} />
);

export type ChoiceboxItemTitleProps = ComponentProps<typeof ItemTitle>;

export const ChoiceboxItemTitle = (props: ChoiceboxItemTitleProps) => (
  <ItemTitle {...props} />
);

export type ChoiceboxItemDescriptionProps = ComponentProps<
  typeof ItemDescription
>;

export const ChoiceboxItemDescription = (
  props: ChoiceboxItemDescriptionProps,
) => <ItemDescription {...props} />;

// Radio button circle (always visible)
export type ChoiceboxItemRadioProps = React.ComponentProps<'div'>;

export const ChoiceboxItemRadio = ({
  className,
  children,
  ...props
}: ChoiceboxItemRadioProps) => (
  <div
    className={cn(
      'border-input flex size-4 shrink-0 items-center justify-center rounded-full border transition-colors',
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

// Check icon (visible only when checked via data-state="checked")
export type ChoiceboxItemIndicatorProps = ComponentProps<'div'>;

export const ChoiceboxItemIndicator = ({
  className,
  ...props
}: ChoiceboxItemIndicatorProps) => (
  <div
    className={cn(
      'bg-purple-brand-400 absolute -top-1 -right-1 z-10 hidden size-4 items-center justify-center rounded-full',
      'group-data-[state=checked]/item:flex',
      className,
    )}
    {...props}
  >
    <CheckIcon className="size-3 text-black" />
  </div>
);
